import { Injectable, OnDestroy } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { createSafeProps } from 'src/util/app-insights';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { filterNull, filterType } from 'src/util/rxjs/operators';
import { Logger } from '../shared/shared-base/amg-ui/logging/logging/logger';
import { AuthProxyService, LogoutEvent } from '../shared/authentication/auth-proxy.service';
import { CurrentUserService } from './current-user.service';

@Injectable({
  providedIn: 'root',
})
export class NavigationTrackerService implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _initialized = false;
  private _isLoggingIn = false;
  private _id = 1;

  constructor(
    private _router: Router,
    private _userService: CurrentUserService,
    private _authProxy: AuthProxyService,
    private _logger: Logger,
  ) {}

  public async init() {
    if (this._initialized) {
      return;
    }
    this._initialized = true;

    // track user login/logout
    this._userService.valueChanges
      .pipe(
        filterNull(),
        distinctUntilChanged((a, b) => a?.email === b?.email),
        takeUntil(this._onDestroy$),
      )
      .subscribe(() => {
        if (this._isLoggingIn) {
          this._logger.info(`User: Login`, createSafeProps({ navId: this.nextId() }));
          this._isLoggingIn = false;
        }
      });
    this._authProxy.events$
      .pipe(filterType(LogoutEvent), takeUntil(this._onDestroy$))
      .subscribe(() => {
        const user = this._userService.value;
        this._logger.info(`User: Logout`, createSafeProps({ navId: this.nextId() }));
      });

    // track user navigation
    this._router.events.pipe(takeUntil(this._onDestroy$)).subscribe((ev) => {
      if (ev instanceof NavigationEnd) {
        if (ev.url.includes('implicit/callback')) {
          this._isLoggingIn = true;
          return;
        }
        this._logger.info(
          `Navigation: Page ${ev.urlAfterRedirects ?? ev.url}`,
          createSafeProps({ navId: this.nextId(), url: ev.urlAfterRedirects ?? ev.url }),
        );
      } else if (ev instanceof NavigationCancel) {
        this._logger.info(
          `Navigation: Cancel ${ev.url}`,
          createSafeProps({ navId: this.nextId(), url: ev.url, reason: ev.reason, code: ev.code }),
        );
      } else if (ev instanceof NavigationError) {
        this._logger.error(
          `Navigation: Error ${ev.url}`,
          createSafeProps({
            navId: this.nextId(),
            url: ev.url,
            error: ev.error,
            target: ev.target,
          }),
        );
      }
    });
  }

  private nextId(): string {
    return String(this._id++);
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
